/* The RK deck queue (W4) — replaces the proposals pane when the
 * `rk_decks` flag is on (probe: the /api/rk/decks namespace 404s while
 * dark). LIST + DETAIL (Tier-2 D1, ruled 2026-09-05): the deck queue is
 * a list of segments on the left — the list IS the pager, so the
 * Prev/Next walk of 94 clicks is gone — and the card of the selected
 * segment on the right, unchanged. Stage-count bar with the fund
 * switch stays (v49). */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import { useListKeys } from '../keyboard/useListKeys'
import { fetchDeckQueue, useRkDecksEnabled } from './api'
import RkDeck from './RkDeck'
import RkSegmentList from './RkSegmentList'
import { stageRank } from './segments'
import { HelpDot } from '../guidance/Guidance'
import { EmptyState, ErrorState, Skeleton } from '../state'
import FundSelector from '../FundSelector'
import { fetchFundLedgers } from '../ledger'
import { openWords, queueNoun, queueOpen, type FundLedgerList } from '../ledgerWords'
import { useFundScope } from '../fundScope'
import type { DeckQueueItem, DeckQueueResponse } from './types'
import './rkdecks.css'
import '../canon.css'

interface FundLite {
  id: string
  name: string
}

/** ``initialDeckId`` seeds the pager on the deck a deep link named
 *  (W6: /v2/workbench/record-keeping?task=<id> lands on THAT walk or
 *  reconcile deck, not on an unfiltered stream). Unknown ids fall back
 *  to the first page rather than dead-ending.
 *
 *  ``titleSelector`` is the fund selector, drawn beside this list's own
 *  title on the standalone route only: inside the record-keeping queue
 *  the queue's title already carries it, and the ruling is ONE control —
 *  there `heading` is false and the list renders no title at all. */
export function RkDecksSurface({
  initialDeckId,
  heading = true,
  titleSelector,
}: {
  initialDeckId?: string | null
  heading?: boolean
  titleSelector?: ReactNode
} = {}) {
  const [queue, setQueue] = useState<DeckQueueResponse | null>(null)
  const [funds, setFunds] = useState<FundLite[]>([])
  /* The fund scope lives in the URL (B6 audit, 2026-09-03): the Workbench
   * landing and every fund-page door arrive as ?fund=<id>, and this
   * surface used to keep its OWN selection starting at "all funds" — so a
   * deep link into one fund silently rendered every fund's segments and
   * only its own menu narrowed it. One source of truth: the search param
   * IS the selection. The pager's "switch fund ▾" menu is RETIRED (Tier-2
   * selector ruling 2026-09-05) — the fund selector in the page title
   * writes the same param, so a fund is chosen the same way everywhere.
   * Absent = all funds. */
  const [params] = useSearchParams()
  const fundId = params.get('fund')
  const bookScopeRef = useRef(fundId)
  bookScopeRef.current = fundId
  const [cur, setCur] = useState(0)
  const [paneled, setPaneled] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // Retry (state system): a fresh read and a fresh heartbeat clock.
  const [tick, setTick] = useState(0)
  const [bookPinned, setBookPinned] = useState<Record<string, DeckQueueItem>>({})
  const queueDecksRef = useRef<Record<string, DeckQueueItem>>({})

  /* `background === true` only from the heartbeat below — the idle clock
   * (lib/api.ts) must not move on a timer. */
  const load = useCallback(
    async (background = false) => {
      try {
        setQueue(await fetchDeckQueue(fundId, background === true))
        setError(null)
      } catch (err: unknown) {
        setError(err)
      }
    },
    [fundId]
  )

  /* Heartbeat (Massimo 2026-08-30: "prod needs to be refreshed any
   * time"): the queue re-fetches itself; nobody presses F5. */
  useEffect(() => {
    load()
    const t = setInterval(() => load(true), 12000)
    return () => clearInterval(t)
  }, [load, tick])
  const retry = () => {
    setError(null)
    setQueue(null)
    setTick((t) => t + 1)
  }

  useEffect(() => {
    queueDecksRef.current = {}
    setBookPinned({})
  }, [fundId])

  useEffect(() => {
    for (const deck of queue?.decks ?? []) queueDecksRef.current[deck.deck_id] = deck
  }, [queue])

  const pinBookDeck = useCallback(
    (deckId: string, pending: boolean, scopeKey: string | null) => {
      if (scopeKey !== bookScopeRef.current) return
      if (!pending) {
        setBookPinned((current) => {
          if (!current[deckId]) return current
          const next = { ...current }
          delete next[deckId]
          return next
        })
        return
      }
      const deck =
        queueDecksRef.current[deckId] ?? queue?.decks.find((item) => item.deck_id === deckId)
      if (deck) setBookPinned((current) => ({ ...current, [deckId]: deck }))
    },
    [queue]
  )

  /* Documents being read right now — the demo console publishes the
   * pipeline count on the same origin; a pulse here answers "how long do
   * I wait" without leaving the page. Fail-soft: no console, no pulse. */
  const [reading, setReading] = useState(0)
  useEffect(() => {
    let alive = true
    const poll = async () => {
      try {
        const r = await fetch('/harness/api/activity')
        if (!r.ok) return
        const j = await r.json()
        if (alive) setReading(Number(j?.docs_processing) || 0)
      } catch {
        /* console absent — stay quiet */
      }
    }
    poll()
    const t = setInterval(poll, 10000)
    return () => {
      alive = false
      clearInterval(t)
    }
  }, [])

  useEffect(() => {
    apiFetch<FundLite[]>('/api/funds')
      .then((list) => setFunds(list.map((f) => ({ id: f.id, name: f.name }))))
      .catch(() => setFunds([]))
  }, [])

  /* Stable presentation order (Massimo 2026-08-30: "cards switching
   * order"): decks keep their FIRST-SEEN position for the session; new
   * arrivals append. Gate/stage changes update badges, never positions. */
  const [seenOrder, setSeenOrder] = useState<string[]>([])
  useEffect(() => {
    const ids = [...(queue?.decks ?? []).map((d) => d.deck_id), ...Object.keys(bookPinned)]
    setSeenOrder((prev) => {
      const kept = prev.filter((id) => ids.includes(id))
      const added = ids.filter((id) => !kept.includes(id))
      return kept.concat(added)
    })
  }, [queue, bookPinned])
  const decks = useMemo(() => {
    const raw = [...(queue?.decks ?? [])]
    for (const deck of Object.values(bookPinned)) {
      if (!raw.some((item) => item.deck_id === deck.deck_id)) raw.push(deck)
    }
    const at = new Map(raw.map((d) => [d.deck_id, d]))
    const ordered = seenOrder.map((id) => at.get(id)).filter((d) => d !== undefined)
    const stable = ordered.length === raw.length ? ordered : raw
    /* The list groups by stage — decisions first, bookings last (D1).
     * The sort is STABLE, so inside a section the queue's own order (and
     * the first-seen order above it) is untouched: a row moves only when
     * its own stage moves, which is the one time it must. */
    return stable
      .map((d, i) => ({ d, i }))
      .sort((a, b) => stageRank(a.d.stage) - stageRank(b.d.stage) || a.i - b.i)
      .map(({ d }) => d)
  }, [bookPinned, queue, seenOrder])
  const total = decks.length

  /* The pager follows the DECK, not the array index: a queue refresh can
   * never switch the card under the officer's eyes. */
  const [curId, setCurId] = useState<string | null>(null)
  useEffect(() => {
    if (curId && !decks.some((d) => d.deck_id === curId)) setCurId(null)
  }, [decks, curId])

  // Seed once, when the named deck is actually in the queue.
  const [seeded, setSeeded] = useState(false)
  useEffect(() => {
    if (seeded || !initialDeckId || decks.length === 0) return
    const at = decks.findIndex((d) => d.deck_id === initialDeckId)
    if (at >= 0) {
      setCur(at)
      setCurId(initialDeckId)
    }
    setSeeded(true)
  }, [seeded, initialDeckId, decks])

  const curIndex = curId ? decks.findIndex((d) => d.deck_id === curId) : -1
  const effIndex = curIndex >= 0 ? curIndex : Math.min(cur, Math.max(0, total - 1))
  const curDeck = decks[effIndex] ?? null

  const fundName = useMemo(() => {
    if (fundId) return funds.find((f) => f.id === fundId)?.name ?? 'Fund'
    const ids = new Set(decks.flatMap((d) => d.fund_ids ?? []))
    if (ids.size === 1) {
      const only = [...ids][0]
      return funds.find((f) => f.id === only)?.name ?? 'All funds'
    }
    return 'All funds'
  }, [fundId, funds, decks])

  /* L-series: the bar reads "N deciding · M booking · K reconciling ·
   * J walking" — the walk is the deck's fourth stage, not a bucket. */
  const counts = queue?.counts
  const countBits = counts
    ? [
        counts.deciding ? `${counts.deciding} deciding` : null,
        counts.booking ? `${counts.booking} booking` : null,
        counts.reconciling ? `${counts.reconciling} reconciling` : null,
        counts.walking ? `${counts.walking} walking` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : ''

  /* ── the list is the cursor (D1) ──────────────────────────────────
   * J / K move the selection through the WHOLE queue and put the focus
   * ring on the row; Enter (the row's own, or the canon hook's when the
   * focus sits on nothing) opens the card and moves into it; Esc comes
   * back to the row. One hook, the keyboard canon's (A5). */
  const listRef = useRef<HTMLDivElement | null>(null)
  const detailRef = useRef<HTMLDivElement | null>(null)

  const rowAt = useCallback(
    (i: number) =>
      listRef.current?.querySelectorAll<HTMLElement>('[data-testid="rk-row"]')[i] ?? null,
    []
  )
  const select = useCallback(
    (i: number, focusRow: boolean) => {
      const next = Math.min(Math.max(0, total - 1), Math.max(0, i))
      setCur(next)
      setCurId(decks[next]?.deck_id ?? null)
      const el = rowAt(next)
      if (el) {
        if (focusRow) el.focus()
        el.scrollIntoView({ block: 'nearest' })
      }
    },
    [total, decks, rowAt]
  )
  const nav = useCallback((d: number) => select(effIndex + d, true), [select, effIndex])
  const onNext = useCallback(() => nav(1), [nav])
  const onPrev = useCallback(() => nav(-1), [nav])
  /** Row click and the row's own Enter: select, then move into the card. */
  const openRow = useCallback(
    (_deckId: string, i: number) => {
      select(i, false)
      detailRef.current?.focus()
    },
    [select]
  )
  const onEnter = useCallback(() => {
    select(effIndex, false)
    detailRef.current?.focus()
  }, [select, effIndex])
  const onEscape = useCallback(() => {
    rowAt(effIndex)?.focus()
  }, [rowAt, effIndex])
  useListKeys({ onNext, onPrev, onEnter, onEscape, enabled: total > 0 })

  /* The card the officer is on stays visible in the list even when the
   * selection moved for him (a settled deck leaves and the next one
   * takes its place). */
  useEffect(() => {
    if (!curDeck) return
    listRef.current
      ?.querySelector<HTMLElement>(`[data-deck-id="${CSS.escape(curDeck.deck_id)}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [curDeck])

  /* The fund rides the LINE only when the bar cannot name one — i.e.
   * when the queue really spans several funds ("All funds"). Repeating
   * one fund's name on every line of its own queue is noise. */
  const multiFund = useMemo(() => new Set(decks.flatMap((d) => d.fund_ids ?? [])).size > 1, [decks])
  const fundNameOf = useCallback(
    (d: { fund_ids?: string[] }) => {
      if (fundId || !multiFund) return null
      const only = (d.fund_ids ?? [])[0]
      if (!only) return null
      return funds.find((f) => f.id === only)?.name ?? null
    },
    [fundId, multiFund, funds]
  )

  return (
    <section className={`rkdecks split${paneled ? ' paneled' : ''}`} data-testid="rk-decks-surface">
      <div className="inner">
        {/* One title per page: inside the Workbench the stream header
         * already names the queue AND carries the one fund selector (the
         * audit's "three stacked titles"). The type-symbol key (Legend
         * canon 2026-08-31, superseded by audit cut 4, Ale 2026-09-05)
         * lives behind the queue's ?; the dots and arrows keep their
         * colours on the cards. */}
        {heading ? (
          <h1>
            {titleSelector ? <>Record proposals — {titleSelector}</> : 'Record proposals'}
            <HelpDot k="queue.record_keeping" />
          </h1>
        ) : null}
        <div className="qbar">
          <span>
            <b>{fundName}</b> · {total} segment{total === 1 ? '' : 's'}
            {countBits ? (
              <>
                : <b>{countBits}</b>
              </>
            ) : null}
            {reading > 0 ? (
              <span className="readingpulse" data-testid="reading-pulse">
                {' '}
                · {reading} document{reading > 1 ? 's' : ''} still being read…
              </span>
            ) : null}
          </span>
        </div>
        {error ? <ErrorState what="proposals" error={error} onRetry={retry} /> : null}
        {total > 0 ? (
          <div className="rksplit">
            <RkSegmentList
              decks={decks}
              selectedId={curDeck?.deck_id ?? null}
              onOpen={openRow}
              fundNameOf={fundNameOf}
              listRef={listRef}
            />
            <div className="rkdetail" data-testid="rk-detail" ref={detailRef} tabIndex={-1}>
              {curDeck ? (
                <RkDeck
                  key={curDeck.deck_id}
                  deckId={curDeck.deck_id}
                  onPaneToggle={setPaneled}
                  /* Auto-advance (Ale 2026-08-19): a settled deck leaves the
                   * queue on reload, so the same position shows the next one —
                   * no extra click. The brief delay lets the ✓ slim land. */
                  onDeckSettled={() => setTimeout(load, 900)}
                  /* D2 (2026-08-31): every decision recomputes the stream
                   * immediately — gate clears and stage moves land on the
                   * queue bar without waiting for the 12s heartbeat. */
                  onDecision={load}
                  onBookPendingChange={(pending) => pinBookDeck(curDeck.deck_id, pending, fundId)}
                />
              ) : null}
            </div>
          </div>
        ) : queue ? (
          <EmptyState
            sentence={`No open proposals — the record is settled for ${
              fundName === 'All funds' ? 'every fund' : fundName
            }.`}
            door={{ label: 'Drop documents ›', to: '/v2/traces' }}
            testId="rk-empty"
          />
        ) : error ? null : (
          /* One deck card's height: pager line + the card. */
          <Skeleton kind="cards" n={1} height={300} className="rk-skel" />
        )}
      </div>
    </section>
  )
}

/** Route element for /v2/proposals — dark while the flag is off. Standing
 *  on its own it wears the fund selector in its title, like every other
 *  page; inside the record-keeping queue the queue's title carries it. */
export default function RkDecksPage() {
  const enabled = useRkDecksEnabled()
  const { fundId, setFund } = useFundScope()
  const [ledgers, setLedgers] = useState<FundLedgerList | null>(null)
  useEffect(() => {
    let cancelled = false
    fetchFundLedgers()
      .then((l) => {
        if (!cancelled) setLedgers(l)
      })
      .catch(() => {
        if (!cancelled) setLedgers(null)
      })
    return () => {
      cancelled = true
    }
  }, [])
  if (enabled === null)
    return (
      <main className="rkdecks">
        <div className="inner">
          <Skeleton kind="cards" n={1} height={300} />
        </div>
      </main>
    )
  if (!enabled)
    return (
      <main className="rkdecks">
        <div className="inner">
          <p className="sub" style={{ marginTop: 24 }}>
            The RK deck queue is not enabled on this deployment.
          </p>
        </div>
      </main>
    )
  return (
    <RkDecksSurface
      titleSelector={
        <FundSelector
          funds={(ledgers?.funds ?? []).map((f) => ({
            id: f.fund_id,
            name: f.fund_name,
            open: queueOpen('record_keeping', f),
            sub: openWords(f),
          }))}
          value={fundId}
          onChoose={setFund}
          noun={queueNoun('record_keeping')}
          loading={!ledgers}
        />
      }
    />
  )
}

/** Embedded single-deck view — the recon-fold rendering the tasks
 * workbench swaps in for ReconcilePane when the flag is on. */
export function RkDeckEmbed({ taskId, onResolved }: { taskId: string; onResolved?: () => void }) {
  const [paneled, setPaneled] = useState(false)
  return (
    <div
      className={`rkdecks${paneled ? ' paneled' : ''}`}
      style={{ height: 'auto', overflow: 'visible' }}
    >
      <div className="inner">
        <RkDeck deckId={`task:${taskId}`} onDeckSettled={onResolved} onPaneToggle={setPaneled} />
      </div>
    </div>
  )
}
